import React, { useState, Fragment, useEffect, useRef, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { ReactSVG } from "react-svg";

import clsx from "clsx";
import { Menu, Transition } from "@headlessui/react";

import Steps from "../../Steps";
import Button from "../../../../common/Button";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import { useDispatch, useSelector } from "react-redux";
import { goalsDataMedicationReview, goalsDataMinorAilment } from "./data";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import CustomInput from "../../../../common/CustomInput";
import {
  updateClinicalService,
  updateMedicationReview,
} from "../../../../../redux/modules/clinical-services/actions";
import SubNavigation from "../../common/sub-navigation";
import { ClinicalServiceTypes, getValidStep } from "../../../text/data";
import {
  capitalizeFirstLetter,
  scrollX,
} from "../../../../../helpers/functions";

const Goals = () => {
  const { clinicalServiceObj, clinicalServiceName, clinicalServiceSteps } =
    useSelector((state) => state.clinicalService);
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [openPersonalGoalsAlert, setOpenPersonalGoalsAlert] = useState(false);
  const [openPersonalGoalsDeleteAlert, setOpenPersonalGoalsDeleteAlert] =
    useState(false);
  const [openHowToAchieveDeleteAlert, setOpenHowToAchieveDeleteAlert] = useState(false);
  const [personalGoals, setPersonalGoals] = useState(null);
  const [howToAchieveGoals, setHowToAchieveGoals] = useState(null);
  const [inputText, setInputText] = useState("");
  const [inputTextHowToAchieve, setInputTextHowToAchieve] = useState("");
  const [personalGoalsModalStep, setPersonalGoalsModalStep] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [editField, setEditField] = useState(null); // 'personal' | 'howTo' | null
  const [flippedCards, setFlipCards] = useState([]);

  const isEditingPersonal = editMode && editField === "personal";
  const isEditingHowTo = editMode && editField === "howTo";
  const modalTitle = isEditingHowTo ? "How to Reach My Goals? (Optional)" : "Personal Goals (Optional)";
  const bothExist = Boolean(personalGoals && howToAchieveGoals);

  // Edit-mode view flags
  const showPersonalOnly = editMode && bothExist && editField === "personal";
  const showHowToOnly = editMode && bothExist && editField === "howTo";
  const showBothInEdit = editMode && !bothExist;

  const personalField = (
    <>
      <CustomInput
        name="personalGoals"
        type="textarea"
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        inputClassName="text-[20px] !text-gray-800 min-h-[120px] max-h-[160px] overflow-y-auto resize-none"
      />
      <p className="text-teal-800 font-medium mt-2 text-sm">
        Personal Goals will be saved to the storyboard take away.
      </p>
    </>
  );

  const howToField = (
    <>
      <h4 className="text-teal-800 font-medium mb-2 font-semibold">How to Reach My Goals? (Optional)</h4>
      <CustomInput
        name="howToAchieveGoals"
        type="textarea"
        value={inputTextHowToAchieve}
        onChange={(e) => setInputTextHowToAchieve(e.target.value)}
        inputClassName="text-[20px] !text-gray-800 min-h-[120px] max-h-[160px] overflow-y-auto resize-none"
      />
    </>
  );

  const handleEditSave = () => {
    const trimmedGoal = inputText?.trim() || "";
    const trimmedHowTo = inputTextHowToAchieve?.trim() || "";

    // Always persist both fields from the unified modal
    setPersonalGoals(trimmedGoal || null);
    setHowToAchieveGoals(trimmedHowTo || null);
    savePersonalGoalsToStorage(trimmedGoal);
    saveHowToAchieveToStorage(trimmedHowTo);

    // If this modal was opened as an "Add" flow (not edit), scroll ahead after saving
    if (!editMode) {
      setOpenPersonalGoalsAlert(false);
      setPersonalGoalsModalStep(1);
      setTimeout(() => {
        scrollAhead();
        setEnableLeftArrow(true);
      }, 400);
    } else {
      closeModalReset();
    }
  };

  const closeModalReset = () => {
    setOpenPersonalGoalsAlert(false);
    setPersonalGoalsModalStep(1);
    setInputText(personalGoals ?? "");
    setInputTextHowToAchieve(howToAchieveGoals ?? "");
    setEditMode(false);
    setEditField(null);
  };
  const [completedFlippedCards, setCompletedFlippedCards] = useState([]);
  const [completedGoalsOfTherapy, setCompletedGoalsOfTherapy] = useState(false);
  const [goalsData, setGoalsData] = useState([]);
  const { showReferral } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [showRef, setShowRef] = useState(showReferral ?? false);
  const [showNextEnabled, setShowNextEnabled] = useState(false);
  const [clicked, setClicked] = useState(false);

  const PERSONAL_GOALS_CARD_INDEX = 1000;
  const HOW_TO_ACHIEVE_CARD_INDEX = 1001;
  const isAMR = clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis;
  const formDataPath = isAMR ? 'treatment' : 'medications';

  const getStorageKeys = () => ({
    flippedCards: `goals_flippedCards_${serviceId}-${currentStep}-${currentSubStep}`,
    personalGoals: `goals_personalGoals_${serviceId}-${currentStep}-${currentSubStep}`,
    howToAchieveGoals: `goals_howToAchieveGoals_${serviceId}-${currentStep}-${currentSubStep}`,
  });

  const savePersonalGoalsToStorage = (value) => {
    const { personalGoals: key } = getStorageKeys();
    value && value.length > 0
      ? sessionStorage.setItem(key, value)
      : sessionStorage.removeItem(key);
  };

  const saveHowToAchieveToStorage = (value) => {
    const { howToAchieveGoals: key } = getStorageKeys();
    value && value.length > 0
      ? sessionStorage.setItem(key, value)
      : sessionStorage.removeItem(key);
  };

  const getFormData = () => {
    const form = clinicalServiceObj?.initialAssessment?.initialAssessmentForm;
    return isAMR ? form?.treatment : form?.medications;
  };

  const updateService = (updatedSteps) => {
    dispatch(isAMR 
      ? updateClinicalService(serviceId, updatedSteps)
      : updateMedicationReview(serviceId, updatedSteps)
    );
  };

  const [enableLeftArrow, setEnableLeftArrow] = useState(false);
  const [enableRightArrow, setEnableRightArrow] = useState(false);

  const updateScrollArrows = (element) => {
    setEnableRightArrow(
      Math.ceil(element.scrollLeft) + element.clientWidth !== element.scrollWidth
    );
    setEnableLeftArrow(element.scrollLeft !== 0);
  };

  const handleScroll = (direction) => {
    const element = document.getElementById("goals");
    element.scrollLeft += direction * 400;
    setTimeout(() => updateScrollArrows(element), 500);
  };

  const scrollAhead = (x = null) => scrollX("goals", x);

  useEffect(() => {
    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis)
      setGoalsData(goalsDataMinorAilment);
    else if (clinicalServiceName === ClinicalServiceTypes.MedicationReview)
      setGoalsData(goalsDataMedicationReview);
  }, [clinicalServiceObj, clinicalServiceName]);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const goalsRef = useRef(null);

  useLayoutEffect(() => {
    const el = goalsRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
      updateScrollArrows(el);
    }
  }, [goalsData, personalGoals, howToAchieveGoals]);

  useEffect(() => {
    const el = goalsRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const inner = goalsRef.current;
      if (inner) {
        setIsOverflowing(inner.scrollWidth > inner.clientWidth);
        updateScrollArrows(inner);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [goalsData, personalGoals, howToAchieveGoals]);

  const onNext = () => {
    if (currentStep > clinicalServiceSteps.length) return;
    
    const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, 1);
    const existingFormData = getFormData();
    
    const updatedSteps = {
      [formDataPath]: {
        ...(existingFormData || {}),
        personalGoals,
        howToAchieveGoals,
        goalOfTherapyComplete: completedGoalsOfTherapy,
      },
      currentStep: nextStep,
      currentSubStep: nextSubStep,
      showReferral: showRef,
      serviceSteps: clinicalServiceSteps,
    };
    
    savePersonalGoalsToStorage(personalGoals);
    saveHowToAchieveToStorage(howToAchieveGoals);
    updateService(updatedSteps);
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, -1);
    updateService({ currentStep: nextStep, currentSubStep: nextSubStep });
  };

  // we should save it on next click along with steps, no need to call same api twice
  // const addPersonalGoals = () => {
  //   const updatedSteps = {
  //     currentStep: currentStep,
  //     currentSubStep: currentSubStep,
  //     treatment: {
  //       personalGoals: personalGoals,
  //     },
  //   };
  //   dispatch(updateClinicalService(serviceId, updatedSteps));
  // };

  useEffect(() => {
    const initialAssessmentForm = clinicalServiceObj?.initialAssessment?.initialAssessmentForm;
    if (!initialAssessmentForm || goalsData.length === 0) return;

    const formData = getFormData();
    if (!formData) return;

    const savedPersonalGoals = formData?.personalGoals ?? "";
    const savedHowToAchieve = formData?.howToAchieveGoals ?? "";
    const isGoalsComplete = !!formData?.goalOfTherapyComplete;

    const { flippedCards: storageKey, personalGoals: personalGoalsStorageKey, howToAchieveGoals: howToAchieveStorageKey } = getStorageKeys();
    const savedStr = sessionStorage.getItem(storageKey);
    const savedCards = savedStr ? JSON.parse(savedStr) : null;
    const savedPersonalGoalsFromStorage = sessionStorage.getItem(personalGoalsStorageKey);
    const savedHowToAchieveFromStorage = sessionStorage.getItem(howToAchieveStorageKey);

    const finalPersonalGoals = (savedPersonalGoals?.length > 0 || savedPersonalGoalsFromStorage?.length > 0)
      ? (savedPersonalGoals || savedPersonalGoalsFromStorage)
      : "";
    const finalHowToAchieve = (savedHowToAchieve?.length > 0 || savedHowToAchieveFromStorage?.length > 0)
      ? (savedHowToAchieve || savedHowToAchieveFromStorage)
      : "";
    const hasPersonalGoals = finalPersonalGoals?.length > 0;
    const hasHowToAchieve = finalHowToAchieve?.length > 0;

    if (personalGoals == null || (finalPersonalGoals && finalPersonalGoals !== personalGoals)) {
      setPersonalGoals(finalPersonalGoals);
      savePersonalGoalsToStorage(finalPersonalGoals);
    }
    if (howToAchieveGoals == null || (finalHowToAchieve && finalHowToAchieve !== howToAchieveGoals)) {
      setHowToAchieveGoals(finalHowToAchieve);
      saveHowToAchieveToStorage(finalHowToAchieve);
    }

    setCompletedGoalsOfTherapy(isGoalsComplete);

    if (savedCards) {
      let updated = savedCards;
      if (!hasPersonalGoals) updated = updated.filter((c) => c !== PERSONAL_GOALS_CARD_INDEX);
      if (!hasHowToAchieve) updated = updated.filter((c) => c !== HOW_TO_ACHIEVE_CARD_INDEX);
      const regularCards = updated.filter((c) => c !== PERSONAL_GOALS_CARD_INDEX && c !== HOW_TO_ACHIEVE_CARD_INDEX);
      setFlipCards(updated);
      setCompletedFlippedCards(regularCards);

      const allCardsFlipped = Array.from({ length: goalsData.length }, (_, i) => i)
        .every((card) => regularCards.includes(card));
      if (allCardsFlipped && !isGoalsComplete) {
        setCompletedGoalsOfTherapy(true);
      }

      if (JSON.stringify(updated) !== savedStr) {
        sessionStorage.setItem(storageKey, JSON.stringify(updated));
      }
    } else {
      const allGoalIndexes = goalsData.map((_, index) => index);
      const initialCards = isGoalsComplete ? allGoalIndexes : [];
      setCompletedFlippedCards(isGoalsComplete ? allGoalIndexes : []);
      setFlipCards(initialCards);
      sessionStorage.setItem(storageKey, JSON.stringify(initialCards));
    }

    setClicked(false);
  }, [
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm,
    clinicalServiceName,
    goalsData,
    serviceId,
    currentStep,
    currentSubStep,
  ]);

  useEffect(() => {
    const hasPersonal = personalGoals != null && personalGoals.length > 0;
    const hasHowTo = howToAchieveGoals != null && howToAchieveGoals.length > 0;
    setShowNextEnabled(hasPersonal || hasHowTo || completedGoalsOfTherapy);
  }, [personalGoals, howToAchieveGoals, completedGoalsOfTherapy]);

  const handleFlipCard = (index, isIconClick = false) => {
    setClicked(true);
    
    const updated = isIconClick
      ? (flippedCards.includes(index) ? [] : [index])
      : flippedCards.includes(index)
        ? flippedCards.filter(item => item !== index)
        : [...flippedCards, index];
    
    setFlipCards(updated);
    
    if (index !== PERSONAL_GOALS_CARD_INDEX && index !== HOW_TO_ACHIEVE_CARD_INDEX) {
      setCompletedFlippedCards((prev) =>
        updated.includes(index)
          ? (prev.includes(index) ? prev : [...prev, index])
          : prev.filter((card) => card !== index)
      );
    }
    
    const { flippedCards: storageKey } = getStorageKeys();
    sessionStorage.setItem(storageKey, JSON.stringify(updated));
  };

  useEffect(() => {
    if (clicked && !completedGoalsOfTherapy && goalsData.length > 0) {
      const allCardsFlipped = Array.from({ length: goalsData.length }, (_, i) => i).every(card => completedFlippedCards.includes(card));
      setCompletedGoalsOfTherapy(allCardsFlipped);
    }
  }, [completedFlippedCards, clicked, completedGoalsOfTherapy, goalsData.length]);

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Goals of Therapy{" "}
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>

              <div className="flex flex-col gap-6">
                <div className="flex min-w-[415px] max-w-0.5 gap-6 pb-4 justify-end">
                  {personalGoals != null && personalGoals.length > 0 && (
                    <div className="h-[52px]"></div>
                  )}
                  {!personalGoals && (
                    <div className="w-[24%] min-w-[415px] max-w-0.5 rounded-[12px] cursor-pointer">
                      <Button
                        text="Add Personal Goals (Optional)"
                        iconUrl="/icons/add.svg"
                        iconBefore
                        className="h-[52px] w-full"
                        onClick={() => {
                          setEditMode(false);
                          setEditField(null);
                          setInputText(personalGoals ?? "");
                          // When adding new personal goals, do NOT prefill the How-to field
                          setInputTextHowToAchieve("");
                          setPersonalGoalsModalStep(1);
                          setOpenPersonalGoalsAlert(true);
                        }}
                      />
                    </div>
                  )}
                </div>
                <div
                  className={`flex min-w-[415px] max-w-0.5 gap-6 overflow-x-auto scroll-smooth `}
                  id="goals"
                >
                  {goalsData.map((goal, index) => {
                    return (
                      <div
                        key={index}
                        className={clsx(
                          "w-1/4 flex flex-col relative justify-center min-w-[415px] max-w-0.5 h-[415px] rounded-[12px] px-[20px] cursor-pointer overflow-hidden",
                          flippedCards.includes(index)
                            ? "bg-gray-20 items-start"
                            : "bg-teal-75 items-center"
                        )}
                        onClick={() => handleFlipCard(index)}
                      >
                        <div
                          className={clsx(
                            `text-teal-300  max-w-[415px] whitespace-normal break-words w-full`,
                            flippedCards.includes(index)
                              ? "text-[24px] font-medium text-left"
                              : "text-[33px] font-bold uppercase text-center"
                          )}
                        >
                          {flippedCards.includes(index) ? (
                            <span>{goal.description}</span>
                          ) : (
                            <>
                              <span>{goal.title.split(" ")[0]}</span>
                              {goal.title.split(" ").length > 1 && (
                                <>
                                  <br />
                                  <span>{goal.title.split(" ").slice(1).join(" ")}</span>
                                </>
                              )}
                            </>
                          )}
                        </div>
                        <ReactSVG
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent the card click event from firing
                            handleFlipCard(index, true);
                          }}
                          src={"/icons/flip.svg"}
                          className="w-[20px] h-[20px] absolute cursor-pointer bottom-[10px] right-[10px]"
                        />
                      </div>
                    );
                  })}
                  {[
                    {
                      key: "personal",
                      cardIndex: PERSONAL_GOALS_CARD_INDEX,
                      title: "Personal Goals",
                      content: personalGoals,
                      show: !!(personalGoals != null && personalGoals.length > 0),
                      deleteOpen: openPersonalGoalsDeleteAlert,
                      setDeleteOpen: setOpenPersonalGoalsDeleteAlert,
                      deleteTitle: "Delete Personal Goals",
                      deleteContent: "Do you want to delete Personal Goals?",
                      onDeleteConfirm: () => {
                        setPersonalGoals("");
                        setInputText("");
                        setInputTextHowToAchieve("");
                        const updated = flippedCards.filter((c) => c !== PERSONAL_GOALS_CARD_INDEX);
                        setFlipCards(updated);
                        const { flippedCards: storageKey } = getStorageKeys();
                        sessionStorage.setItem(storageKey, JSON.stringify(updated));
                        savePersonalGoalsToStorage("");
                        // persist deletion so it doesn't reappear on navigation
                        const existingFormData = getFormData();
                        const updatedSteps = {
                          [formDataPath]: {
                            ...(existingFormData || {}),
                            personalGoals: "",
                          },
                        };
                        updateService(updatedSteps);
                        setOpenPersonalGoalsDeleteAlert(false);
                        setOpenPersonalGoalsAlert(false);
                      },
                    },
                    {
                      key: "howToAchieve",
                      cardIndex: HOW_TO_ACHIEVE_CARD_INDEX,
                      title: "How to Reach My Goals?",
                      content: howToAchieveGoals,
                      show: !!(howToAchieveGoals != null && howToAchieveGoals.length > 0),
                      deleteOpen: openHowToAchieveDeleteAlert,
                      setDeleteOpen: setOpenHowToAchieveDeleteAlert,
                      deleteTitle: "Delete How to Reach My Goals?",
                      deleteContent: "Do you want to delete how to reach my goals?",
                      onDeleteConfirm: () => {
                          setHowToAchieveGoals(null);
                          setInputTextHowToAchieve("");
                          setInputText("");
                          const updated = flippedCards.filter((c) => c !== HOW_TO_ACHIEVE_CARD_INDEX);
                          setFlipCards(updated);
                          const { flippedCards: storageKey } = getStorageKeys();
                          sessionStorage.setItem(storageKey, JSON.stringify(updated));
                          saveHowToAchieveToStorage("");
                          // persist deletion so it doesn't reappear on navigation
                          const existingFormData = getFormData();
                          const updatedSteps = {
                            [formDataPath]: {
                              ...(existingFormData || {}),
                              howToAchieveGoals: "",
                            },
                          };
                          updateService(updatedSteps);
                          setOpenHowToAchieveDeleteAlert(false);
                          setOpenPersonalGoalsAlert(false);
                      },
                    },
                  ]
                    .filter((card) => card.show)
                    .map((card) => (
                      <div
                        key={card.key}
                        className={clsx(
                          "w-1/4 flex flex-col relative justify-center min-w-[415px] max-w-0.5 h-[415px] rounded-[12px] px-[20px] cursor-pointer overflow-hidden",
                          flippedCards.includes(card.cardIndex)
                            ? "bg-gray-20 items-start pl-8"
                            : "bg-teal-75 items-center"
                        )}
                        onClick={() => handleFlipCard(card.cardIndex)}
                      >
                        <div className="absolute top-[10px] right-[10px] cursor-pointer">
                          <Menu as="div" className="relative inline-block text-left">
                              <div>
                              <Menu.Button onClick={(e) => e.stopPropagation()}>
                                <ReactSVG
                                  src="/icons/big-triple-dot.svg"
                                  className="flex text-black justify-end w-10 p-4"
                                />
                              </Menu.Button>
                            </div>
                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-100"
                              enterFrom="transform translate-y-2 opacity-0 scale-95"
                              enterTo="transform translate-y-0 opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform translate-y-0 opacity-100 scale-100"
                              leaveTo="transform translate-y-2 opacity-0 scale-95"
                            >
                              <Menu.Items className="absolute right-0 mt-1 mr-4 w-24 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                                <Menu.Item>
                                  <button
                                    className="block w-full text-left px-3 py-3 border-b border-b-neutral-70 hover:bg-gray-300"
                                    onClick={() => {
                                      // Preserve step state; set edit mode and which field the user intends to edit
                                      setEditMode(true);
                                      setEditField(card.key === "personal" ? "personal" : "howTo");
                                      setInputText(personalGoals ?? "");
                                      setInputTextHowToAchieve(howToAchieveGoals ?? "");
                                      setOpenPersonalGoalsAlert(true);
                                    }}
                                  >
                                    Edit
                                  </button>
                                </Menu.Item>
                                <Menu.Item>
                                  <button
                                    className="block w-full text-left px-3 py-3 border-b border-b-neutral-70 hover:bg-gray-300"
                                    onClick={() => card.setDeleteOpen(true)}
                                  >
                                    Delete
                                  </button>
                                </Menu.Item>
                              </Menu.Items>
                            </Transition>
                          </Menu>
                        </div>
                        <p
                          className={clsx(
                            "text-teal-300 text-center whitespace-normal break-words w-full",
                            flippedCards.includes(card.cardIndex)
                              ? "text-[24px] font-medium"
                              : "text-[33px] font-bold uppercase"
                          )}
                        >
                          {flippedCards.includes(card.cardIndex)
                            ? capitalizeFirstLetter(card.content)
                            : <span>{card.title}</span>}
                        </p>
                        <ReactSVG
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFlipCard(card.cardIndex, true);
                          }}
                          src="/icons/flip.svg"
                          className="w-[20px] h-[20px] absolute cursor-pointer bottom-[10px] right-[10px]"
                        />
                        <ConfirmationModalNew
                          open={card.deleteOpen}
                          title={card.deleteTitle}
                          cancelBtnType="primary-outline"
                          type="primary"
                          confirmText="Delete"
                          cancelText="Cancel"
                          content={() => card.deleteContent}
                          className="font-semibold text-gray-800 text-lg w-[537px]"
                          onCross={() => card.setDeleteOpen(false)}
                          handleClose={() => card.setDeleteOpen(false)}
                          confirmAction={card.onDeleteConfirm}
                        />
                      </div>
                    ))}
                  {isOverflowing && (
                    <>
                      {enableLeftArrow && (
                        <button
                          className="absolute m-auto bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
                          onClick={() => handleScroll(-1)}
                        >
                          <ReactSVG
                            src="/icons/arrow-right.svg"
                            className="w-[15px] rotate-180"
                          />
                        </button>
                      )}
                      {enableRightArrow && (
                        <button
                          className="absolute m-auto right-0 bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
                          onClick={() => handleScroll(1)}
                        >
                          <ReactSVG
                            src="/icons/arrow-right.svg"
                            className="w-[15px]"
                          />
                        </button>
                      )}
                    </>
                  )}
                  <ConfirmationModalNew
                    open={openPersonalGoalsAlert}
                    title={modalTitle}
                    type={"primary"}
                    hideActions={true}
                    className="font-semibold text-gray-800 !text-[22px] w-[720px]"
                    titleClassName="text-teal-800 font-medium"
                    onCross={closeModalReset}
                    handleClose={closeModalReset}
                    content={() =>
                      editMode ? (
                        <div className="space-y-6">
                          {personalField}
                          <div className="h-[1px] bg-gray-300" />
                          {howToField}

                          <div className="flex gap-3 pt-4">
                            <button
                              type="button"
                              className="flex-1 h-[52px] rounded-[8px] border border-REELTeal text-teal-300 bg-white"
                              onClick={closeModalReset}
                            >
                              Cancel
                            </button>

                            <button
                              type="button"
                              className="flex-1 h-[52px] rounded-[8px] bg-REELTeal text-white"
                              onClick={handleEditSave}
                            >
                              Update & Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            {!(isEditingHowTo) && (
                              <>
                                <CustomInput
                                  name="personalGoals"
                                  placeholder="Enter personal goals (Optional)"
                                  type="textarea"
                                  value={inputText}
                                  label=""
                                  onChange={(e) => setInputText(e.target.value)}
                                  inputClassName="text-[20px] !text-gray-800 min-h-[120px] max-h-[160px] overflow-y-auto resize-none"
                                />
                                <p className="text-teal-800 font-medium mt-2 text-sm">
                                  Personal Goals will be saved to the storyboard take away.
                                </p>
                              </>
                            )}
                            {/* action row for step 1 */}
                            {personalGoalsModalStep === 1 && (
                              <div className="mt-4 flex gap-3">
                                {!editMode && personalGoals && personalGoals.length > 0 && (
                                  <button
                                    type="button"
                                    className="flex-1 h-[52px] rounded-[8px] border border-red-500 text-red-500 bg-white"
                                    onClick={() => setOpenPersonalGoalsDeleteAlert(true)}
                                  >
                                    Delete
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="flex-1 h-[52px] rounded-[8px] border border-REELTeal text-teal-300 bg-white"
                                  onClick={closeModalReset}
                                >
                                  Cancel
                                </button>
                                {isEditingPersonal ? (
                                  <button
                                    type="button"
                                    className="flex-1 h-[52px] rounded-[8px] bg-REELTeal text-white"
                                    onClick={() => {
                                      if (!inputText?.trim()) {
                                        setPersonalGoals("");
                                        savePersonalGoalsToStorage("");
                                      } else {
                                        setPersonalGoals(inputText || null);
                                        savePersonalGoalsToStorage(inputText || "");
                                      }
                                      closeModalReset();
                                    }}
                                  >
                                    Save
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="flex-1 h-[52px] rounded-[8px] bg-REELTeal text-white"
                                    onClick={() => {
                                      if (!inputText?.trim()) return;
                                      // Save personal goals immediately, then go to step 2
                                      setPersonalGoals(inputText || null);
                                      savePersonalGoalsToStorage(inputText || "");
                                      setPersonalGoalsModalStep(2);
                                    }}
                                  >
                                    {(personalGoals && inputText?.trim().length > 0 && inputText !== (personalGoals ?? "")) ? "Update & Continue" : "Save & Continue"}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          {personalGoalsModalStep === 2 && (
                            <div>
                              {!(isEditingHowTo) && (
                                <>
                                  <div className="h-[1px] bg-gray-300 mb-6"></div>
                                  <h4 className="text-teal-800 font-medium mb-2 font-semibold text-lg">How to reach my goals? (optional)</h4>
                                </>
                              )}
                              <CustomInput
                                name="howToAchieveGoals"
                                placeholder="Enter goals"
                                type="textarea"
                                value={inputTextHowToAchieve}
                                label=""
                                onChange={(e) => setInputTextHowToAchieve(e.target.value)}
                                inputClassName="text-[20px] !text-gray-800 min-h-[120px] max-h-[160px] overflow-y-auto resize-none"
                              />
                              <div className="mt-4 flex gap-3">
                                {isEditingHowTo ? (
                                  <>
                                    <button
                                      type="button"
                                      className="flex-1 h-[52px] rounded-[8px] border border-REELTeal text-teal-300 bg-white"
                                      onClick={closeModalReset}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="flex-1 h-[52px] rounded-[8px] bg-REELTeal text-white"
                                      onClick={() => {
                                        setPersonalGoals(inputText || null);
                                        setHowToAchieveGoals(inputTextHowToAchieve?.length ? inputTextHowToAchieve : null);
                                        savePersonalGoalsToStorage(inputText || "");
                                        saveHowToAchieveToStorage(inputTextHowToAchieve || "");
                                        closeModalReset();
                                      }}
                                    >
                                      Save
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      title="Back to Personal Goals"
                                      aria-label="Back to Personal Goals"
                                      className="flex-1 h-[52px] rounded-[8px] border border-REELTeal text-teal-300 bg-white"
                                      onClick={() => {
                                        // Back: go to personal goals step to allow editing and prefill saved value
                                        setPersonalGoalsModalStep(1);
                                        setInputText(personalGoals ?? "");
                                        setInputTextHowToAchieve("");
                                      }}
                                    >
                                      Back
                                    </button>
                                    <button
                                      type="button"
                                      className="flex-1 h-[52px] rounded-[8px] border border-REELTeal text-teal-300 bg-white"
                                      onClick={() => {
                                        // Skip: save personal goals but leave howToAchieve empty
                                        setPersonalGoals(inputText || null);
                                        setHowToAchieveGoals(null);
                                        savePersonalGoalsToStorage(inputText || "");
                                        saveHowToAchieveToStorage("");
                                        setOpenPersonalGoalsAlert(false);
                                        setPersonalGoalsModalStep(1);
                                        setTimeout(() => {
                                          scrollAhead();
                                          setEnableLeftArrow(true);
                                        }, 400);
                                      }}
                                    >
                                      Skip
                                    </button>
                                    <button
                                      type="button"
                                      className="flex-1 h-[52px] rounded-[8px] bg-REELTeal text-white"
                                      onClick={() => {
                                        // Save: persist both personal goals and how-to text
                                        setPersonalGoals(inputText || null);
                                        setHowToAchieveGoals(inputTextHowToAchieve?.length ? inputTextHowToAchieve : null);
                                        savePersonalGoalsToStorage(inputText || "");
                                        saveHowToAchieveToStorage(inputTextHowToAchieve || "");
                                        setOpenPersonalGoalsAlert(false);
                                        setPersonalGoalsModalStep(1);
                                        setTimeout(() => {
                                          scrollAhead();
                                          setEnableLeftArrow(true);
                                        }, 400);
                                      }}
                                    >
                                      {howToAchieveGoals && inputTextHowToAchieve?.trim().length > 0 && inputTextHowToAchieve !== (howToAchieveGoals ?? "") ? "Update & Save" : "Save"}
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onPrevious}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] ${
              !showNextEnabled ? "opacity-40 pointer-events-none" : ""
            }`}
            onClick={onNext}
          />
        </div>
      </div>
    </div>
  );
};

export default Goals;
